import AdminPermission from "components/admin/AdminPermission";
import useAdminAccess from "hooks/useAdminAccess";
import {
  httpReviewHeldOTCTransaction,
  httpFetchOTCStats,
  httpFetchOTCTransactions,
} from "api/admin/transaction";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import Filter from "components/ui/Filter";
import Pagination from "components/ui/Pagination";
import Spinner from "components/ui/Spinner";
import Table from "components/ui/Table";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import currency, { moneyFormat, getUserCurrency } from "utilities/currency";
import { cryptos } from "utilities/wallets";

const statuses = [
  { label: "All", value: "all" },
  { label: "Successful", value: "successful" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Pending", value: "pending" },
  { label: "Manual review", value: "pending_manual_review" },
];

const transactionTypes = [
  { label: "All", value: "all" },
  { label: "Sell", value: "sell" },
  { label: "Withdrawal", value: "withdrawal" },
  { label: "Receive", value: "receive" },
  { label: "OTC In", value: "otc-in" },
  { label: "OTC Out", value: "otc-out" },
];

const cryptoFilters = [
  { label: "All", value: "all" },
  ...cryptos.map((crypto) => ({
    label: crypto.toUpperCase(),
    value: crypto,
  })),
];

const limit = 50;

const OTCTransactions: React.FC = () => {
  const { can } = useAdminAccess();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<any>(null);
  const [comment, setComment] = useState("");
  const review = useMutation(
    (action: "release" | "reject") =>
      httpReviewHeldOTCTransaction(selected.id, action, comment),
    {
      onSuccess: () => {
        setSelected(null);
        setComment("");
        queryClient.invalidateQueries(["otcTransactions"]);
        queryClient.invalidateQueries(["otcStats"]);
      },
    },
  );

  const [status, setStatus] = useState("all");
  const [crypto, setCrypto] = useState("all");
  const [transactionType, setTransactionType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch OTC Stats
  const { data: statsData, isLoading: statsLoading } = useQuery(
    ["otcStats"],
    () => httpFetchOTCStats(),
    { enabled: can("otc.stats.view"), initialData: { data: null } },
  );

  // Fetch OTC Transactions
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery(
    [
      "otcTransactions",
      currentPage,
      status,
      crypto,
      transactionType,
      startDate,
      endDate,
    ],
    () =>
      httpFetchOTCTransactions(
        currentPage,
        limit,
        status !== "all" ? status : undefined,
        undefined,
        crypto !== "all" ? crypto : undefined,
        startDate || undefined,
        endDate || undefined,
        transactionType !== "all" ? transactionType : undefined,
      ),
    {
      initialData: { data: { transactions: [] } },
    },
  );

  const stats = useMemo(() => statsData || {}, [statsData]);
  const transactions = useMemo(
    () => transactionsData?.transactions || [],
    [transactionsData],
  );
  const total = useMemo(() => transactionsData?.total || 0, [transactionsData]);
  const page = useMemo(() => transactionsData?.page || 1, [transactionsData]);

  const formattedStats = useMemo(
    () => ({
      completedTrades: stats.total_completed_trades || 0,
      failedTrades: stats.total_failed_trades || 0,
      fiatInflow: moneyFormat(stats.total_fiat_inflow_amount || 0),
      fiatOutflow: moneyFormat(stats.total_fiat_outflow_amount || 0),
    }),
    [stats],
  );

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <h3 className="mb-8 text-2xl">OTC Transaction Management</h3>

        {/* Stats Section */}
        <AdminPermission permission={"otc.stats.view"}>
          <div className="grid grid-cols-4 gap-4 mb-10">
            <div className="p-6 rounded-lg bg-orange-50">
              <p className="mb-2 text-sm text-gray-600">Completed Trades</p>
              <p className="text-2xl font-semibold">
                {statsLoading ? <Spinner /> : formattedStats.completedTrades}
              </p>
            </div>
            <div className="p-6 rounded-lg bg-orange-50">
              <p className="mb-2 text-sm text-gray-600">Failed Trades</p>
              <p className="text-2xl font-semibold">
                {statsLoading ? <Spinner /> : formattedStats.failedTrades}
              </p>
            </div>
            <div className="p-6 rounded-lg bg-orange-50">
              <p className="mb-2 text-sm text-gray-600">Total Fiat Inflow</p>
              <p className="text-2xl font-semibold">
                {statsLoading ? (
                  <Spinner />
                ) : (
                  `${currency("ngn")} ${formattedStats.fiatInflow}`
                )}
              </p>
            </div>
            <div className="p-6 rounded-lg bg-orange-50">
              <p className="mb-2 text-sm text-gray-600">Total Fiat Outflow</p>
              <p className="text-2xl font-semibold">
                {statsLoading ? (
                  <Spinner />
                ) : (
                  `${currency("ngn")} ${formattedStats.fiatOutflow}`
                )}
              </p>
            </div>
          </div>
        </AdminPermission>

        {/* Filters Section */}
        <div className="flex items-center mt-5 mb-8 gap-x-5">
          <div>
            <div className="mb-2 text-sm">Crypto:</div>
            <Filter data={cryptoFilters} setValue={setCrypto} value={crypto} />
          </div>

          <div>
            <div className="mb-2 text-sm">Transaction Type:</div>
            <Filter
              data={transactionTypes}
              setValue={setTransactionType}
              value={transactionType}
            />
          </div>

          <div>
            <div className="mb-2 text-sm">Status:</div>
            <Filter data={statuses} setValue={setStatus} value={status} />
          </div>

          <div>
            <label htmlFor="startDate" className="block mb-2 text-sm">
              Start Date:
            </label>
            <input
              id="startDate"
              type="date"
              className="py-1 border border-gray-400 rounded"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block mb-2 text-sm">
              End Date:
            </label>
            <input
              id="endDate"
              type="date"
              className="py-1 border border-gray-400 rounded"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        {selected && (
          <section
            aria-label="OTC transaction review"
            className="p-5 mb-6 border rounded-lg"
          >
            <div className="flex justify-between">
              <h4>
                Transaction #{selected.id}: {selected.transactionType}
              </h4>
              <button type="button" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>
            <p>
              {selected.amount}{" "}
              {selected.currencyType === "naira"
                ? selected.currency || getUserCurrency(selected)
                : selected.crypto?.toUpperCase()}{" "}
              · {selected.status}
            </p>
            <p className="break-all">
              Reference: {selected.transactionID || "—"}
            </p>
            {selected.place === "external" && (
              <p>
                On-chain confirmation:{" "}
                {selected.confirmedAt ? "Confirmed" : "Awaiting confirmation"}.
                Deposited funds cannot be cancelled.
              </p>
            )}
            {selected.status === "pending_manual_review" &&
              (selected.balanceSettledAt || selected.withdrawalReview) && (
                <p>
                  This withdrawal was already debited. Review it in Withdrawal
                  Reviews.
                </p>
              )}
            {((selected.status === "pending_manual_review" &&
              !selected.balanceSettledAt) ||
              (selected.status === "pending" &&
                selected.transactionType === "withdrawal" &&
                selected.balanceSettledAt)) &&
              !selected.withdrawalReview &&
              can("otc.onboarding.approve") && (
                <div className="mt-4 space-y-3">
                  <label htmlFor="otc-review-comment">Review comment</label>
                  <textarea
                    id="otc-review-comment"
                    className="block w-full p-2 border"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    disabled={review.isLoading}
                  />
                  <button
                    type="button"
                    className="mr-4 underline"
                    disabled={
                      review.isLoading ||
                      !comment.trim() ||
                      (selected.place === "external" && !selected.confirmedAt)
                    }
                    onClick={() => review.mutate("release")}
                  >
                    {selected.status === "pending"
                      ? "Retry dispatch"
                      : "Release"}
                  </button>
                  {selected.status === "pending_manual_review" &&
                    selected.place !== "external" && (
                      <button
                        type="button"
                        className="underline"
                        disabled={review.isLoading || !comment.trim()}
                        onClick={() => review.mutate("reject")}
                      >
                        Reject
                      </button>
                    )}
                  {review.isError && (
                    <p role="alert">
                      Review failed. Refresh the transaction and check the error
                      before retrying.
                    </p>
                  )}
                </div>
              )}
          </section>
        )}

        {/* Transactions Table Section */}
        <div className="w-full">
          {transactionsLoading ? (
            <div className="flex items-center justify-center w-full h-64">
              <Spinner className="text-5xl" />
            </div>
          ) : transactions.length > 0 ? (
            <>
              <Table
                header={[
                  "User",
                  "Amount",
                  "Type",
                  "Crypto",
                  "Status",
                  "Date & Time",
                ]}
                data={transactions.map((transaction: any) => [
                  transaction.userName || transaction.receiver || "-",
                  transaction.amount || "-",
                  transaction.transactionType || "-",
                  transaction.currencyType === "naira"
                    ? transaction.currency || getUserCurrency(transaction)
                    : transaction.crypto?.toUpperCase() || "-",
                  transaction.status || "-",
                  `${DateTime.fromISO(transaction.timestamp).toFormat(
                    "DD",
                  )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`,
                  () => {
                    setSelected(transaction);
                    setComment("");
                    review.reset();
                  },
                ])}
              />
              <Pagination
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                limit={limit}
                page={page}
                total={total}
              />
            </>
          ) : (
            <div className="py-10 text-center text-gray-500">
              No transactions found
            </div>
          )}
        </div>
      </Main>
    </Container>
  );
};

export default OTCTransactions;
